import {
    Container, Dialog,
    DialogContent, Divider, Grid, IconButton, Typography
} from "@material-ui/core";
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import {
    Add as AddIcon, BackspaceOutlined as BackspaceOutlinedIcon,
    Delete as DeleteIcon, Search as SearchIcon
} from "@material-ui/icons";
import clsx from "clsx";
import MUIDataTable from "mui-datatables";
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from "react";
import { useForm } from 'react-hook-form';
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from 'react-router-dom';
import TextField from '../../components/Form/TextField';
import AlertDialog from '../../components/ui/AlertDialog';
import ButtonUI from "../../components/ui/ButtonUI";
import LoadingUI from "../../components/ui/LoadingUI";
import PageTitle from "../../components/ui/PageTitleUI";
import { useAuth } from '../../hooks/auth';
import VehicleTypeApi from "../../services/VehicleTypeApi";
import getDefaultGridOptions from '../../utils/componentUtils';
import utilsDate from "../../utils/Date";
import permissions from "../../utils/Permissions";
import useKeypress from '../../utils/useKeypress';
import { DialogTitle, PaperComponent, useStyles } from "./Style";
import VehicleTypeForm from './VehicleTypeForm';

interface IFormInput {
    code: any;
    description: any;
}

interface Params {
    openPanelOnLoad: boolean;
}

const VehicleType: React.FC = () => {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation<Params>();
    const [vehicleTypes, setVehicleTypes] = useState<any>();
    const [loading, setLoading] = useState(true);
    const [openedPainel, setOpenedPainel] = React.useState(false);
    const [vehicleTypeCode, setVehicleTypeCode] = useState<string>("");
    const { user, hasPermission } = useAuth();
    const { t } = useTranslation("VehicleType");
    const { t: _t } = useTranslation("General");
    const { enqueueSnackbar } = useSnackbar();
    const { control, handleSubmit, reset, getValues } = useForm<IFormInput>(
        {
            defaultValues: { code: '', description: '' }
        }
    )
    const [, setSuccess] = useState(false);

    const searchVehicleTypes = (data: any) => {
        setLoading(true);
        data.pageSize = 1000;
        // data.showDeleted = false
        VehicleTypeApi.searchVehicleType(data)
            .then((result) => {
                if (result && result.items) {
                    setVehicleTypes(result);
                } else {
                    setVehicleTypes([]);
                }
            })
            .catch(() => {
                setVehicleTypes([]);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!hasPermission(permissions.VEHICLE_TYPE.Search))
            history.replace('NoPermission');
    }, [history, user])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        setVehicleTypes([]);
        searchVehicleTypes(getValues());
    }, []);

    const onSubmit = (data: any) => {
        searchVehicleTypes(data);
    };

    const centerTableHead = clsx({
        [classes.tableHeadCenter]: true,
    });

    const columns = [
        {
            name: "code",
            label: String(t("code")),
            options: {
                sort: true,

                setCellProps: () => ({
                    align: "left",
                }),
            },
        },
        {
            name: "description",
            label: String(t("description")),

            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    className: centerTableHead,
                }),
                setCellProps: () => ({
                    align: "center",
                }),
            },
        },
        {
            name: "updatedBy",
            label: String(t("updatedBy")),
            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    className: centerTableHead,
                }),
                setCellProps: () => ({
                    align: "center",
                }),
            },
        },
        {
            name: "updatedAt",
            label: String(t("updatedAt")),
            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    className: centerTableHead,
                }),
                setCellProps: () => ({
                    align: "center",
                }),
                customBodyRender: (
                    value: any,
                    tableMeta: any,
                    updateValue: any
                ) => {
                    return utilsDate.DateShort(value);
                },
            },
        },
        {
            name: "",
            options: {
                setCellProps: () => ({
                    align: "right",
                }),
                disableClick: true,
                sort: false,
                customBodyRender: (
                    value: any,
                    tableMeta: any,
                    updateValue: any
                ) => renderButtons(tableMeta.rowData[0]),
            },
        },
    ];

    const renderButtons = (code: string) => {
        return (
            <AlertDialog
                dialogTitle={t("dialogDeleteTitle")}
                dialogContentText={t("dialogDeleteContentText")}
                cancelTextButton={t("cancelDeleteTextButton")}
                confirmTextButton={t("confirmDeleteTextButton")}
                confirmDialogFunction={() => {
                    handleDeleteItem(code);
                }}
                Button={
                    <IconButton size="small" disabled={!hasPermission(permissions.VEHICLE_TYPE.Delete)} edge="end">
                        <DeleteIcon />
                    </IconButton>
                }
            />
        );
    };

    function handleDeleteItem(code: string) {
        setLoading(true);
        VehicleTypeApi.deleteVehicleType(code)
            .then((result) => {
                enqueueSnackbar(t("returnSubmitDeleteFormSuccess"), {
                    variant: "success",
                });
            })
            .finally(() => {
                searchVehicleTypes(getValues());
            });
    }

    useKeypress("Escape", () => {
        setOpenedPainel(false);
    });

    const handleOpenPanelChange = (code: string) => {
        setVehicleTypeCode(code);
        setOpenedPainel(true);
    };

    const callbackDialogHandle = () => {
        setOpenedPainel(false);
        searchVehicleTypes(getValues());
    };

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {
                    cursor: vehicleTypes?.items ? 'pointer' : ''                    
                }
            }
        }
    })

    useEffect(() => {
        if (location.state?.openPanelOnLoad)
        {
            setOpenedPainel(true); 
            setVehicleTypeCode(''); 
        }
    }, [location.state?.openPanelOnLoad])

    return (
        <Container maxWidth={false} component="main">
            <Dialog
                open={openedPainel}
                aria-labelledby="form-dialog-title"
                PaperComponent={PaperComponent}
                onBackdropClick={() => setOpenedPainel(false)}
                fullWidth
            >
                <DialogTitle
                    classes={{ root: classes.dialogTitle }}
                    id="draggable-dialog-title"
                    onClose={() => setOpenedPainel(false)}
                >
                    {vehicleTypeCode !== "" ? t("editTitle") : t("addTitle")}
                </DialogTitle>
                <DialogContent>
                    <VehicleTypeForm
                        vehicleTypeCode={vehicleTypeCode}
                        handleSetSuccess={setSuccess}
                        clickHandlerCalbkack={callbackDialogHandle}
                        clickHandlerClose={() => setOpenedPainel(false)}
                    />
                </DialogContent>
            </Dialog>

            <PageTitle title={t("vehicleType")} />
            <div className={classes.rootContent}>
                <div className={classes.paper}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={1}>
                            <Grid
                                item
                                xs={6}
                                style={{
                                    padding: "0px",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                <Typography className={classes.root}>
                                    {t("titleSearch")}
                                </Typography>
                            </Grid>

                            <Grid
                                item
                                xs={6}
                                style={{ padding: "0px", textAlign: "right" }}
                            >
                                <ButtonUI
                                    color="secondary"
                                    onClick={() => {
                                        setOpenedPainel(true);
                                        setVehicleTypeCode("");
                                    }}
                                    disabled={!hasPermission(permissions.VEHICLE_TYPE.Add)}
                                    startIcon={<AddIcon />}
                                >
                                    {t("add")}
                                </ButtonUI>
                                <ButtonUI
                                    onClick={() => reset()}
                                    startIcon={<BackspaceOutlinedIcon />}
                                >
                                    {t("clean")}
                                </ButtonUI>
                                <ButtonUI
                                    type="submit"
                                    color="primary"
                                    startIcon={<SearchIcon />}
                                >
                                    {t("search")}
                                </ButtonUI>
                            </Grid>
                        </Grid>

                        <Divider style={{ marginTop: "10px" }} />

                        <Grid container spacing={1}>
                            <Grid item xs={1}>
                                <TextField
                                    label={t("code")}
                                    name="code"
                                    control={control}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    label={t("description")}
                                    name="description"
                                    control={control}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </form>

                    {loading ? (
                        <LoadingUI />
                    ) : (
                        <MuiThemeProvider theme={myTheme}>
                            <MUIDataTable
                                title=""
                                data={vehicleTypes?.items}
                                columns={columns}
                                options={{
                                    ...getDefaultGridOptions(_t),
                                    serverSide: false,
                                    pagination: vehicleTypes?.items
                                        ? true
                                        : false,
                                    sort: vehicleTypes?.items ? true : false,
                                    selectToolbarPlacement: "none",
                                    filter: false,
                                    search: false,
                                    download: false,
                                    print: false,
                                    viewColumns: false,
                                    selectableRows: "none",
                                    onCellClick: (
                                        _colData,
                                        { colIndex, dataIndex }
                                    ) => {
                                        if (hasPermission(permissions.VEHICLE_TYPE.Edit))
                                            if (
                                                vehicleTypes &&
                                                vehicleTypes.items &&
                                                colIndex < 5
                                            ) {
                                                handleOpenPanelChange(
                                                    vehicleTypes.items[dataIndex]
                                                        .code
                                                );
                                            }
                                    },
                                }}
                            />
                        </MuiThemeProvider>
                    )}
                </div>
            </div>
        </Container>
    );
};
export default VehicleType;

